<template>
  <div class="pa-2" style="width: 50%">
    <v-toolbar density="compact" color="grey-lighten-3">
      <v-tabs v-model="tab">
        <v-tab
          v-for="(_, i) in plan?.individual_asset_management_plans ?? []"
          :key="i"
        >
          Plan {{ i + 1 }}
        </v-tab>
      </v-tabs>
    </v-toolbar>
    <v-divider />
    <v-tabs-window :model-value="tab">
      <v-tabs-window-item
        v-for="(individualPlan, i) in plan?.individual_asset_management_plans ??
        []"
        :value="i"
        :key="i"
      >
        <v-toolbar density="compact" color="grey-lighten-4">
          <v-toolbar-title>Plan {{ i + 1 }}</v-toolbar-title> <v-spacer />
        </v-toolbar>
        <div class="pa-4">
          <RichText
            class="mb-4"
            :model-value="individualPlan.management_consideration_factors"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]
                ?.management_consideration_factors
            "
            :label="`5.${i + 1}.1 Management Consideration Factors`"
            readonly
            :options="{ diff }"
          />
          <RichText
            class="mb-4"
            :model-value="individualPlan.condition_monitoring"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]
                ?.condition_monitoring
            "
            :label="`5.${i + 1}.2 Condition Monitoring`"
            readonly
            :options="{ diff }"
          />
          <RichText
            class="mb-4"
            :model-value="individualPlan.maintenance_defect_management"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]
                ?.maintenance_defect_management
            "
            :label="`5.${i + 1}.3 Maintenance & Defect Management`"
            readonly
            :options="{ diff }"
          />
          <RichText
            class="mb-4"
            :model-value="individualPlan.maintenance_plans"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]?.maintenance_plans
            "
            :label="`5.${i + 1}.4 Maintenance Plans`"
            readonly
            :options="{ diff }"
          />
          <v-card class="mb-4" variant="outlined" rounded="lg">
            <v-toolbar density="compact">
              <v-toolbar-title>
                5.{{ i + 1 }}.5 Preventative Maintenance Tactics
              </v-toolbar-title>
              <v-spacer />
              <span>
                # Assets Applied:
                {{ preventativeMaintenance.num_assets_applied }} /
                {{ preventativeMaintenance.num_assets }},
              </span>
              <span class="ml-3">
                # Compliance:
                {{ preventativeMaintenance.num_workorders }} /
                {{ preventativeMaintenance.num_tactic_ops }}
              </span>
              <v-tooltip text="Refresh" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="ml-3"
                    icon="mdi-reload"
                    density="compact"
                    :loading="loadingPreventative"
                    @click="loadPreventativeMaintenance"
                  />
                </template>
              </v-tooltip>
            </v-toolbar>
            <v-table class="tactics-table" fixed-header density="compact">
              <thead>
                <tr>
                  <th class="text-left">Maint Plan ID</th>
                  <th class="text-left">Maint Plan Desc</th>
                  <th class="text-left">Asset</th>
                  <th class="text-left">Stat Flag</th>
                  <th class="text-left">Num Done (last 12 months)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(tactic, i) in preventativeMaintenance.tactics"
                  :key="i"
                >
                  <td>{{ tactic.maint_plan_id }}</td>
                  <td>{{ tactic.maint_plan_description }}</td>
                  <td>{{ tactic.asset_name }}</td>
                  <td></td>
                  <td>
                    {{ tactic.num_workorders }} / {{ tactic.num_tactic_ops }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
          <RichText
            class="mb-4"
            :model-value="individualPlan.statutary_requirements"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]
                ?.statutary_requirements
            "
            :label="`5.${i + 1}.6 Statutary Requirements`"
            readonly
            :options="{ diff }"
          />
          <RichText
            class="mb-4"
            :model-value="individualPlan.equipment_decomissioning_disposal"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]
                ?.equipment_decomissioning_disposal
            "
            :label="`5.${i + 1}.7 Equipment Decomissioning & Disposal`"
            readonly
            :options="{ diff }"
          />
          <RichText
            class="mb-4"
            :model-value="individualPlan.capital_projects"
            :diff-value="
              diffPlan?.individual_asset_management_plans[i]?.capital_projects
            "
            :label="`5.${i + 1}.8 Capital Projects`"
            readonly
            :options="{ diff }"
          />
        </div>
      </v-tabs-window-item>
    </v-tabs-window>
  </div>
</template>

<script setup lang="ts">
import type { AssetManagementPlan } from "@/classes/asset-management-plan";
import { ref, watch } from "vue";
import RichText from "@/components/base/inputs/RichText.vue";
import { PreventativeMaintenanceData } from "@/store/asset-management-plan";
import axiosInstance from "@/plugins/axios";

const props = defineProps<{
  plan: AssetManagementPlan | null;
  diffPlan?: AssetManagementPlan | null;
  diff?: boolean;
}>();

const tab = ref(0);

const preventativeMaintenance = ref<PreventativeMaintenanceData>({
  tactics: [],
  num_assets_applied: 0,
  num_assets: 0,
  num_workorders: 0,
  num_tactic_ops: 0,
});

const $axios = axiosInstance;

const loadingPreventative = ref(false);
const loadPreventativeMaintenance = async () => {
  loadingPreventative.value = true;
  preventativeMaintenance.value =
    await $axios.$post<PreventativeMaintenanceData>(
      "/asset-management-plan/preventative-maintenance-data",
      {
        floc_ids:
          props.plan?.floc_boundaries.map((b) => b.functional_location_id) ??
          [],
      },
    );
  loadingPreventative.value = false;
};

watch(
  () => props.plan?.id,
  async () => {
    await loadPreventativeMaintenance();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.tactics-table {
  max-height: 400px;
}
</style>
